import { JsonObject } from '@bufbuild/protobuf';
import * as lodash from 'lodash';
import { NEVER, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatInput } from '@angular/material/input';

import { AutocompleteOption } from '../../interfaces';
import { ParentControlErrorStateMatcher } from '../../utilities';

@Component({
  selector: 'phoenix-server-side-autocomplete',
  template: `
    @if (!loadingList) {
      <div class="w-full relative">
        <mat-form-field class="w-full">
          <mat-label>{{ title }}</mat-label>
          <input
            type="text"
            matInput
            [name]="title.replace(' ', '').toLocaleLowerCase()"
            [attr.aria-label]="'inputServerSideAutocomplete'"
            [formControl]="control"
            [errorStateMatcher]="errorMatcher!"
            (input)="refreshSelected()"
            (focus)="onFocus()"
            (paste)="paste($event)"
            (blur)="onBlur()"
            [matAutocomplete]="auto"
            #autocompleteInput
          />
          <div class="flex items-center" matSuffix>
            @if (isLoading) {
              <mat-spinner class="mx-[12px]" [diameter]="24"></mat-spinner>
            }

            @if (valueNotFound) {
              <mat-icon
                class="theme-text-warning mx-[4px]"
                aria-label="Value not found"
                matTooltip="This record was not found. Check it or select a different one."
                >warning</mat-icon
              >
            }

            @if (showClearButton && control.value && control.enabled) {
              <button
                type="button"
                mat-icon-button
                aria-label="Clear"
                (click)="clearInput()"
              >
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          <!-- Error Messages -->
          @if (control.errors && errorMessages) {
            <mat-error>
              <small>{{ errorMessages[errorKey] }}</small>
            </mat-error>
          } @else if (control.errors) {
            <mat-error>
              <small>{{ control | formError }}</small>
            </mat-error>
          } @else if (parentControl?.errors) {
            <mat-error>
              <small>{{
                errorMessages?.[parentErrorKey] ?? (parentControl! | formError)
              }}</small>
            </mat-error>
          }

          <!-- Hints -->
          @if (hintTemplate) {
            <mat-hint>
              <ng-container *ngTemplateOutlet="hintTemplate"></ng-container>
            </mat-hint>
          }

          <mat-autocomplete
            #auto="matAutocomplete"
            autoActiveFirstOption
            [displayWith]="displayValue"
            (optionSelected)="optionSelected($event)"
          >
            @for (option of optionList; track option) {
              <mat-option
                [disabled]="isReadOnly"
                [value]="attrToSave ? option[attrToSave] : option"
              >
                @if (iscustomMatOption && optionHtml) {
                  <div class="w-full" [innerHTML]="optionHtml(option)"></div>
                } @else {
                  {{ displayValue(option) }}
                }
              </mat-option>
            }
            @if (showNoOptionsMessage) {
              <mat-option disabled>
                {{ noOptionsMessage }}
              </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </div>
    } @else {
      <ngx-skeleton-loader />
    }
  `,
  standalone: false,
})
export class ServerSideAutocompleteComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Output() valueChange = new EventEmitter();
  @Output() selectedOptionChange = new EventEmitter<any>();
  /**
   * Emits after a `refresh`-driven lookup settles: `true` when the control's
   * value matched an option, `false` when it did not and was flagged as not found.
   * Nothing is emitted when the lookup fails, since validity is unknown.
   */
  @Output() valueResolved = new EventEmitter<boolean>();
  @Input() optionHtml?: (option: any) => string;
  @Input() attrFilter: string | null = null;
  @Input() attrFilterBackup!: string;
  @Input() attrToSave: string | null = null;
  @Input() customFilter?: (data: AutocompleteOption | string) => string;
  @Input() title = '';
  @Input() serverCallback!: (value: string) => Observable<any>;
  @Input() pasteCallback?: (value: string) => Observable<any[]>;
  @Input() formatDataResponse?: (value: any) => any;
  @Input() errorMessages!: any;
  @Input() control: FormControl = new FormControl('');
  /**
   * Shows the skeleton in place of the field. Also set internally while a
   * `refresh` lookup is in flight, so a parent that binds `refresh` must leave
   * this input alone.
   */
  @Input() loadingList = false;
  @Input() cleanAfterSelect = false;
  @Input() showClearButton = false;
  @Input() sortOptions?: (data: any[]) => any[];
  @Input() disabled = false;
  @Input() isReadOnly = false;
  @Input() autoFocus = false;
  @Input() fetchOnFocus = false;
  @Input() noOptionsMessage = '';
  @Input() hintTemplate?: TemplateRef<unknown>;

  /**
   * Emit to resolve the value currently held by `control` into a selected
   * option, using `serverCallback`. Saves the parent from looking the value up
   * itself when it comes from outside (query params, a loaded record).
   *
   * Must be a stable reference — a new observable is resubscribed, not merged.
   * Binding this input also resolves once on init, so an emission that lands
   * before this component subscribes cannot leave the value unresolved.
   */
  refresh = input<Observable<void>>(NEVER);

  private _parentControl?: FormControl;
  @Input() set parentControl(control: FormControl | undefined) {
    this._parentControl = control;
    this.syncRequiredMarker();
  }
  get parentControl(): FormControl | undefined {
    return this._parentControl;
  }

  errorMatcher: ErrorStateMatcher = new ErrorStateMatcher();

  @ViewChild('autocompleteInput')
  autocompleteInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger?: MatAutocompleteTrigger;

  // Syncs the control's required state to the Material input marker.
  private _matInput?: MatInput;
  @ViewChild(MatInput) set matInput(input: MatInput | undefined) {
    this._matInput = input;
    this.syncRequiredMarker();
  }
  get matInput(): MatInput | undefined {
    return this._matInput;
  }

  iscustomMatOption = false;
  _selectedOption!: any;
  @Input() set selectedOption(data: any) {
    if (data) {
      this._selectedOption = data;
      this.optionList = [data];
    } else {
      this.optionList = [];
    }
  }

  optionList: any[] = [];
  isLoading = false;
  hasLoadedOptions = false;
  /**
   * Set when `resolveValue` found no option for the control's value. The value
   * is kept (and survives blur) so the user can see and correct it; editing,
   * selecting or clearing the input drops the flag.
   */
  valueNotFound = false;

  destroy$ = new Subject<void>();
  private controlDestroy$ = new Subject<void>();
  private resolveCancel$ = new Subject<void>();

  constructor() {
    // switchMap resubscribes if the parent swaps the observable reference.
    // A parent may bind a value that is not there yet, so fall back to NEVER
    // rather than letting switchMap throw on a nullish source.
    toObservable(this.refresh)
      .pipe(
        switchMap(refresh => refresh ?? NEVER),
        takeUntilDestroyed()
      )
      .subscribe(() => this.resolveValue());
  }

  ngOnInit() {
    this.iscustomMatOption = !!this.optionHtml;
    this.setupControlSubscription();

    // Resolves up front when a parent supplied a refresh trigger, so the value
    // is fetched even if that trigger fired before this component subscribed.
    if (!!this.refresh() && this.refresh() !== NEVER) {
      this.resolveValue();
    }
  }

  /**
   * Looks the control's current value up through `serverCallback` and selects
   * the matching option. When nothing matches, the value is kept and flagged
   * via `valueNotFound` so the user can review it instead of losing it. A
   * failed lookup leaves the value untouched — the server not answering says
   * nothing about its validity.
   */
  resolveValue(): void {
    if (!this.serverCallback) {
      return;
    }

    // Cancels any in-flight lookup so a stale response for an older value can
    // never be applied to the current one.
    this.resolveCancel$.next();

    const value = this.control.value;
    if (!value) {
      this.valueNotFound = false;
      this._selectedOption = null;
      this.optionList = [];
      return;
    }

    this.loadingList = true;
    let failed = false;
    this.serverCallback(value)
      .pipe(
        catchError(() => {
          failed = true;
          return of([]);
        }),
        finalize(() => {
          this.loadingList = false;
        }),
        takeUntil(this.resolveCancel$),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        if (failed) {
          return;
        }

        const formattedData = this.formatDataResponse
          ? this.formatDataResponse(data)
          : data;

        // Picks the option whose saved attribute equals the control value; the
        // server may return neighbours of the search term, not just an exact hit.
        const match = formattedData?.find((option: JsonObject) =>
          lodash.isEqual(
            this.attrToSave ? option[this.attrToSave] : option,
            value
          )
        );

        this.valueNotFound = !match;
        if (match) {
          this.selectedOption = match;
          this.optionList = [match];
        } else {
          this._selectedOption = null;
          this.optionList = [];
        }

        this.valueResolved.emit(!!match);
      });
  }

  private setupControlSubscription(): void {
    this.controlDestroy$.next();

    this.control.valueChanges
      .pipe(
        takeUntil(this.controlDestroy$),
        // Any new value, including one set from outside, voids a not-found
        // flag raised for the previous value.
        tap(() => {
          this.valueNotFound = false;
        }),
        debounceTime(500),
        tap(() => {
          this.optionList = [];
          this.hasLoadedOptions = false;
          this.isLoading = true;
        }),
        switchMap(value => {
          if (value) {
            return this.serverCallback(value).pipe(
              finalize(() => {
                this.isLoading = false;
              })
            );
          } else {
            this.isLoading = false;
            return [];
          }
        })
      )
      .subscribe(data => {
        const formattedData = this.formatDataResponse
          ? this.formatDataResponse(data)
          : data;

        if (this.sortOptions) {
          this.optionList = this.sortOptions(formattedData);
        } else {
          this.optionList = formattedData;
        }
        this.hasLoadedOptions = true;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      if (this.disabled && this.control.enabled) {
        this.control.disable();
      } else if (!this.disabled && this.control.disabled) {
        this.control.enable();
      }
    }

    if (changes['control'] && !changes['control'].firstChange) {
      this.setupControlSubscription();
      this.syncRequiredMarker();
    }

    if (changes['parentControl'] && this.parentControl) {
      this.errorMatcher = new ParentControlErrorStateMatcher(
        this.parentControl
      );
    }
  }

  ngAfterViewInit() {
    if (this.autoFocus && this.autocompleteInput) {
      setTimeout(() => {
        this.autocompleteInput.nativeElement.focus();
      }, 100);
    }
  }

  get errorKey(): string {
    return Object.keys(this.control.errors || {})[0];
  }

  get parentErrorKey(): string {
    return Object.keys(this.parentControl?.errors ?? {})[0];
  }

  // The field is required when either its own control or the parent control
  // carries the required validator.
  get required(): boolean {
    return (
      this.control.hasValidator(Validators.required) ||
      (this.parentControl?.hasValidator(Validators.required) ?? false)
    );
  }

  private syncRequiredMarker(): void {
    const input = this._matInput;
    if (!input) return;

    Promise.resolve().then(() => {
      input.required = this.required;
      input.stateChanges.next();
    });
  }

  get showNoOptionsMessage(): boolean {
    return (
      !!this.noOptionsMessage &&
      this.hasLoadedOptions &&
      !this.isLoading &&
      !this.optionList.length
    );
  }

  displayValue = (value: any): string => {
    value = this.selectedValue(value);

    if (!value) return '';
    if (this.customFilter) {
      return this.customFilter(value);
    }
    return this.attrFilter && value[this.attrFilter]
      ? value[this.attrFilter]
      : this.attrFilterBackup && value[this.attrFilterBackup]
        ? value[this.attrFilterBackup]
        : value.toString();
  };

  selectedValue(value: unknown) {
    if (!value) return '';
    if (this.attrToSave && typeof value !== 'object') {
      const savedValue = this.foundSelected(value);
      return savedValue ?? value;
    }
    return value;
  }

  foundSelected(value: unknown) {
    return this.optionList.find(obj => {
      return lodash.isEqual(
        this.attrToSave ? (obj as AutocompleteOption)[this.attrToSave] : obj,
        value
      );
    });
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    this.valueNotFound = false;
    this._selectedOption = value;
    this.selectedOptionChange.emit(this._selectedOption);
    this.valueChange.emit(value);
    if (this.cleanAfterSelect) {
      this.control.setValue('');
    }
  }

  onFocus() {
    if (!this.fetchOnFocus || this.control.value || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasLoadedOptions = false;
    this.serverCallback('')
      .pipe(
        catchError(() => of([])),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        const formattedData = this.formatDataResponse
          ? this.formatDataResponse(data)
          : data;

        this.optionList = this.sortOptions
          ? this.sortOptions(formattedData)
          : formattedData;
        this.hasLoadedOptions = true;
        this.autocompleteTrigger?.openPanel();
      });
  }

  paste(event: ClipboardEvent) {
    if (!this.pasteCallback) {
      return;
    }

    const pastedData = event.clipboardData?.getData('text/plain');
    if (!pastedData) {
      return;
    }

    event.preventDefault();
    this.control.setValue(pastedData, { emitEvent: false });
    this.isLoading = true;
    this.pasteCallback(pastedData)
      .pipe(
        catchError(() => of([])),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(values => {
        values.forEach(value => this.valueChange.emit(value));
        this.control.setValue('');
        this.refreshSelected();
      });
  }

  refreshSelected() {
    this.valueNotFound = false;
    this._selectedOption = null;
    this.selectedOptionChange.emit(this._selectedOption);
  }

  clearInput() {
    this.control.setValue('');
    this.valueChange.emit('');
    this.refreshSelected();
    this.displayValue('');
    this.optionList = [];
  }

  onBlur() {
    // Marks the field touched so its validation (e.g. required) surfaces,
    // matching standard Material form-field behavior.
    this.control.markAsTouched();
    this.parentControl?.markAsTouched();

    // A value flagged as not found is kept so the user can review it.
    if (!this._selectedOption && !this.valueNotFound) {
      this.control.setValue('');
    }
  }

  ngOnDestroy(): void {
    this.resolveCancel$.complete();
    this.controlDestroy$.next();
    this.controlDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
